import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlockIcon from '@mui/icons-material/Block';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DrawIcon from '@mui/icons-material/Draw';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExploreOffIcon from '@mui/icons-material/ExploreOff';
import HomeIcon from '@mui/icons-material/Home';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LockIcon from '@mui/icons-material/Lock';
import LockClockIcon from '@mui/icons-material/LockClock';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import React from 'react';
import { ALLIANCE_APPS } from './alliance-apps';
import { CURRENT_APP } from './current-app';
import {
  SystemAction,
  SystemDateChip,
  SystemIdentity,
  SystemScreen,
  ViewingAsChip,
} from './system-screen';

/**
 * Thin configs over SystemScreen, one per case, with the copy from the
 * Claude Design SystemScreen spec: SCREENS with the DMD replacements, and
 * SCREENS_DMD (onboarding, mfa, pending). Apps pass in the handlers and
 * identity; nothing here reads auth state.
 */

const CONTRACTING_URL = ALLIANCE_APPS.contracting.url;

/** "DMD Contracting", linked, for body copy */
const contractingLink = (
  <a href={CONTRACTING_URL} target="_blank" rel="noopener noreferrer">
    {ALLIANCE_APPS.contracting.fullName}
  </a>
);

const signOutAction = (onSignOut: () => void): SystemAction => ({
  label: 'Sign out',
  icon: <LogoutIcon />,
  onClick: onSignOut,
});

const goToContracting: SystemAction = {
  label: 'Go to Contracting',
  href: CONTRACTING_URL,
  external: true,
};

const retryAction = (onRetry: () => void): SystemAction => ({
  label: 'Try again',
  icon: <RefreshIcon />,
  onClick: onRetry,
});

/** Signed-out-safe props shared by the gate screens */
interface GateScreenProps {
  identity?: SystemIdentity | null;
  onSignOut: () => void;
}

/**
 * Admins without MFA. Dashboard can't set it up, so this sends them to
 * Contracting. Agents and staff get OnboardingScreen instead, which lists
 * MFA as a step.
 */
export function MfaRequiredScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="info"
      icon={<PhonelinkLockIcon />}
      eyebrow="Security"
      title="Set up multi‑factor authentication"
      body={
        <p>
          Every DMD account needs a second sign-in step. {CURRENT_APP.name}{' '}
          can’t set it up, so sign in to {contractingLink}, choose a method,
          then come back here.
        </p>
      }
      identity={props.identity}
      primaryAction={goToContracting}
      secondaryAction={signOutAction(props.onSignOut)}
      help
    />
  );
}

/**
 * Agents and staff who haven't finished account setup, which happens in
 * Contracting (DMD Onboarding, row 6b). Lists the steps left.
 *
 * @param steps - The steps still to do, e.g. "Set up multi-factor
 *   authentication", "Add a card for your technology fee"
 */
export function OnboardingScreen(props: GateScreenProps & { steps: string[] }) {
  return (
    <SystemScreen
      layout="page"
      tone="info"
      icon={<VerifiedUserIcon />}
      eyebrow="Account setup"
      title="Finish setting up your account"
      body={
        <p>
          Before you can use {CURRENT_APP.fullName}, finish account setup in{' '}
          {contractingLink}. It only takes a few minutes, and you’ll only do it
          once.
        </p>
      }
      bullets={props.steps}
      identity={props.identity}
      primaryAction={goToContracting}
      secondaryAction={signOutAction(props.onSignOut)}
      help
    />
  );
}

/**
 * The Agent Agreement isn't signed. A blocking screen (Dashboard keeps it
 * full page rather than the design's banner, Oct 15) with the banner's copy.
 * Continue lets them in for this visit; it comes back on the next load.
 */
export function AgreementScreen(
  props: GateScreenProps & { onContinue: () => void },
) {
  return (
    <SystemScreen
      layout="page"
      tone="info"
      icon={<DrawIcon />}
      eyebrow="Agent Agreement"
      title="You have not signed the Agent Agreement yet"
      body={<p>Sign it in {contractingLink}.</p>}
      identity={props.identity}
      primaryAction={goToContracting}
      secondaryAction={{
        label: `Continue to ${CURRENT_APP.name}`,
        icon: <ArrowForwardIcon />,
        onClick: props.onContinue,
      }}
      help
    />
  );
}

/** Agent status Pending: limited access until they finish in Contracting */
export function AccountPendingScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="warning"
      icon={<HourglassTopIcon />}
      title="Your account is pending"
      body={
        <p>
          Your DMD account is pending. You’ll have limited access until you:
        </p>
      }
      bullets={[
        `Sign in to ${ALLIANCE_APPS.contracting.fullName}`,
        'Sign the Annual Agent Agreement',
      ]}
      identity={props.identity}
      primaryAction={goToContracting}
      secondaryAction={signOutAction(props.onSignOut)}
      help
    />
  );
}

/** Agent status Inactive */
export function AccountInactiveScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="warning"
      icon={<PauseCircleIcon />}
      title="Your account is inactive"
      body={
        <p>
          Your DMD account is not able to be accessed at this time due to
          inactivity. Please contact your manager or the Help Desk.
        </p>
      }
      identity={props.identity}
      secondaryAction={signOutAction(props.onSignOut)}
      help="include"
    />
  );
}

/** /authorize returned 403 */
export function NotAuthorizedScreen(props: GateScreenProps) {
  return (
    <SystemScreen
      layout="page"
      tone="error"
      icon={<BlockIcon />}
      title="You don’t have access"
      body={
        <p>
          Your DMD account is not able to be accessed at this time. Please
          contact your manager or the Help Desk.
        </p>
      }
      identity={props.identity}
      secondaryAction={signOutAction(props.onSignOut)}
      help="include"
    />
  );
}

/** Agent status Terminated: deliberately minimal */
export function AccessDeniedScreen(props: { onSignOut: () => void }) {
  return (
    <SystemScreen
      layout="page"
      tone="neutral"
      icon={<LockIcon />}
      title="Access has been denied."
      secondaryAction={signOutAction(props.onSignOut)}
      showLinks={false}
    />
  );
}

/** /authorize failed for another reason; shows the API message when there is one */
export function AuthFailScreen(
  props: GateScreenProps & {
    onRetry: () => void;
    serverMessage?: string | null;
  },
) {
  return (
    <SystemScreen
      layout="page"
      tone="error"
      icon={<CloudOffIcon />}
      title="We couldn’t load your account"
      body={
        <p>
          Something went wrong while loading your account. Try again, and if it
          keeps happening, contact the Help Desk.
        </p>
      }
      serverMessage={props.serverMessage}
      identity={props.identity}
      primaryAction={retryAction(props.onRetry)}
      secondaryAction={signOutAction(props.onSignOut)}
      help
    />
  );
}

/**
 * Maintenance for agents. The end time lives in the date chip so it can
 * change without rewriting copy.
 *
 * @param until - Formatted end time, e.g. "Saturday, Oct 4 · 6:00 AM EDT"
 * @param onSignOut - Omit when nobody is signed in
 */
export function MaintenanceScreen(props: {
  until: string | null;
  identity?: SystemIdentity | null;
  onSignOut?: () => void;
}) {
  return (
    <SystemScreen
      layout="page"
      tone="warning"
      icon={<EngineeringIcon />}
      eyebrow="IT maintenance update"
      title="We’ll be back soon"
      body={
        <p>
          We’re performing scheduled maintenance to improve your experience.
          {props.until ? ' Please check back on:' : ' Please check back soon.'}
        </p>
      }
      detail={
        props.until ? <SystemDateChip>{props.until}</SystemDateChip> : null
      }
      identity={props.identity}
      secondaryAction={props.onSignOut ? signOutAction(props.onSignOut) : null}
      help
    />
  );
}

/**
 * Render crash. Inline when the page failed but the shell didn't; page when
 * the shell itself failed. No error code is shown.
 */
export function CrashScreen(props: {
  layout: 'page' | 'inline';
  onRetry: () => void;
  onHome: () => void;
}) {
  return (
    <SystemScreen
      layout={props.layout}
      tone="error"
      icon={<ErrorOutlineIcon />}
      title="Something went wrong"
      body={
        <p>
          This page ran into a problem. We’re working on it and we’ll get it
          fixed as soon as we can.
        </p>
      }
      primaryAction={retryAction(props.onRetry)}
      secondaryAction={{
        label: 'Go home',
        icon: <HomeIcon />,
        onClick: props.onHome,
      }}
    />
  );
}

/** Unknown route, inline in the shell instead of redirecting home */
export function NotFoundScreen(props: { onHome: () => void }) {
  return (
    <SystemScreen
      layout="inline"
      tone="neutral"
      icon={<ExploreOffIcon />}
      eyebrow="Page not found"
      title="We couldn’t find that page"
      body={
        <p>
          The link may be out of date, or the page may have moved. If you were
          looking for the other DMD app, try this:
        </p>
      }
      appList
      primaryAction={{
        label: 'Go home',
        icon: <HomeIcon />,
        onClick: props.onHome,
      }}
    />
  );
}

/**
 * Session expired, full page (when the app can't render). Neutral, no
 * eyebrow, identity or Help Desk, so it can't be mistaken for MFA.
 */
export function SessionExpiredScreen(props: { onSignIn: () => void }) {
  return (
    <SystemScreen
      layout="page"
      tone="neutral"
      icon={<LockClockIcon />}
      title="Your session has expired"
      body={<p>Please sign in again.</p>}
      primaryAction={{
        label: 'Sign in',
        icon: <LoginIcon />,
        onClick: props.onSignIn,
      }}
    />
  );
}

/**
 * A page's data didn't load. Offline (their side, neutral) or server
 * trouble (ours, warning), picked by navigator.onLine.
 *
 * @param inShell - The page's surface (no difference in DMD's light apps)
 */
export function LoadFailedScreen(props: {
  layout: 'page' | 'inline';
  onRetry: () => void;
  inShell?: boolean;
}) {
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  return (
    <SystemScreen
      layout={props.layout}
      inShell={props.inShell}
      tone={offline ? 'neutral' : 'warning'}
      icon={offline ? <WifiOffIcon /> : <CloudOffIcon />}
      eyebrow="Couldn’t load this page"
      title={
        offline
          ? 'You appear to be offline'
          : 'We’re having trouble reaching our servers'
      }
      body={
        <p>
          {offline
            ? 'Check your connection and try again.'
            : 'Please try again in a moment.'}
        </p>
      }
      primaryAction={retryAction(props.onRetry)}
    />
  );
}

/**
 * Every request for the impersonated agent came back 403. Shown in the page
 * with a lime Stop impersonating.
 */
export function NoAgentScreen(props: {
  agent: { name: string; id: string };
  onStopImpersonating: () => void;
}) {
  return (
    <SystemScreen
      layout="inline"
      tone="imp"
      icon={<VisibilityOffIcon />}
      eyebrow={<ViewingAsChip name={props.agent.name} id={props.agent.id} />}
      title="You don’t have access to this agent’s data"
      body={
        <p>
          Your account isn’t allowed to act for this agent, so this page can’t
          load their information. Nothing is missing from their account.
        </p>
      }
      primaryAction={{
        label: 'Stop impersonating',
        icon: <PersonOffIcon />,
        onClick: props.onStopImpersonating,
        imp: true,
      }}
    />
  );
}

/** A lazy chunk failed to load after a deploy */
export function AppUpdatedScreen(props: { onReload?: () => void }) {
  return (
    <SystemScreen
      layout="inline"
      tone="info"
      icon={<SystemUpdateAltIcon />}
      title={`${CURRENT_APP.name} has been updated`}
      body={<p>Reload to get the latest version. You won’t lose your place.</p>}
      primaryAction={{
        label: 'Reload',
        icon: <RefreshIcon />,
        onClick: props.onReload || (() => window.location.reload()),
      }}
    />
  );
}

/**
 * One section (tab, drawer, panel) failed; the rest of the page stays put.
 *
 * @param onRetry - Reloads only that section
 * @param dense - Inside a drawer
 * @param message - The API message, when there is one
 * @param inShell - The page's surface (no difference in DMD's light apps)
 */
export function SectionErrorScreen(props: {
  onRetry?: () => void;
  dense?: boolean;
  message?: string | null;
  inShell?: boolean;
}) {
  return (
    <SystemScreen
      layout="compact"
      inShell={props.inShell}
      tone="error"
      icon={<ErrorOutlineIcon />}
      title="This section didn’t load."
      serverMessage={props.message}
      dense={props.dense}
      primaryAction={props.onRetry ? retryAction(props.onRetry) : null}
    />
  );
}
